import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

export enum TokenStoreKeys {
  tokenExpiry = 'token_expiry',
}

// Time thresholds in milliseconds
const TOKEN_WARNING_THRESHOLD_MS = 3 * 60 * 1000; // 3 minutes before expiry

/**
 * Stores the token expiry timestamp
 */
export const setTokenExpiry = async (expiryTimestamp: number): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.setItem(TokenStoreKeys.tokenExpiry, expiryTimestamp.toString());
  } else {
    await SecureStore.setItemAsync(TokenStoreKeys.tokenExpiry, expiryTimestamp.toString(), {
      keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
    });
  }
};

/**
 * Retrieves the stored token expiry timestamp
 */
export const getTokenExpiry = async (): Promise<number | null> => {
  try {
    let expiryStr: string | null = null;

    if (Platform.OS === 'web') {
      expiryStr = localStorage.getItem(TokenStoreKeys.tokenExpiry);
    } else {
      expiryStr = await SecureStore.getItemAsync(TokenStoreKeys.tokenExpiry);
    }

    if (expiryStr != null && expiryStr !== '') {
      const expiry = parseInt(expiryStr, 10);
      return isNaN(expiry) ? null : expiry;
    }

    return null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to get token expiry from storage:', error);
    return null;
  }
};

/**
 * Removes the stored token expiry timestamp
 */
export const removeTokenExpiry = async (): Promise<void> => {
  try {
    if (Platform.OS === 'web') {
      localStorage.removeItem(TokenStoreKeys.tokenExpiry);
    } else {
      await SecureStore.deleteItemAsync(TokenStoreKeys.tokenExpiry);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to remove token expiry from storage:', error);
  }
};

/**
 * Checks if the token has expired
 */
export const checkTokenExpired = (expiryTimestamp: number): boolean => {
  return Date.now() >= expiryTimestamp;
};

/**
 * Checks if the current time is within the warning threshold for a given expiry timestamp
 */
export const isTimeWithinWarningThreshold = (expiryTimestamp: number): boolean => {
  const now = Date.now();
  const timeUntilExpiry = expiryTimestamp - now;
  return timeUntilExpiry <= TOKEN_WARNING_THRESHOLD_MS && timeUntilExpiry > 0;
};
